// TreckDeductionPdfDoc.ts
//
// Fast, synchronous PDF builder for the Treck deduction export, using jsPDF +
// jspdf-autotable. This replaces @react-pdf/renderer, whose Yoga flexbox layout
// and per-node font shaping made large (hundreds/thousands of rows) tables take
// several seconds and froze the tab (the whole render runs synchronously on the
// main thread). autotable writes cell text directly with no layout engine, so
// even thousands of rows render in a few hundred milliseconds — fast enough to
// run inline on the main thread without a Web Worker.
import { jsPDF } from 'jspdf'
import { autoTable } from 'jspdf-autotable'
import type { CellHookData } from 'jspdf-autotable'

// Chargeback type identifiers (kept local — no dependency on the app's
// ConstantData file, which is large and pulls in unrelated modules). These match
// the literal values produced by getChargebackType() in the search hook.
const CHARGEBACK_TYPE_RTVS = 'RTVS'
const CHARGEBACK_TYPE_CBDC = 'CBDC'
const CHARGEBACK_TYPE_CB = 'CB'

// ─── Types ───────────────────────────────────────────────────────────────────

type ColumnDef = { headerName: string; field: string }

export type GeneratePdfParams = {
  treckHeader?: ColumnDef[]
  rowData?: any[]
  headerData?: any
  chargebackType?: string
  vendorId?: string
  documentId?: string
  paymentId?: string
  vendorName?: string
}

type CardRow = {
  label: string
  value: string
  negative?: boolean
  bold?: boolean
}
type Card = { title: string; rows: CardRow[] }

type ColumnPlan = { path: string[]; isCurrency: boolean; isEnum: boolean }

// ─── Value helpers ─────────────────────────────────────────────────────────────

function formatCurrency(value: any): string {
  if (value === null || value === undefined || value === '') return ''
  const n =
    typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''))
  if (Number.isNaN(n)) return String(value)
  return (
    (n < 0 ? '-' : '') +
    '$' +
    Math.abs(n).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  )
}

function isCurrencyField(field: string): boolean {
  const f = field.toLowerCase()
  return (
    f.includes('amount') ||
    f.includes('cost') ||
    f.includes('fee') ||
    f.includes('price') ||
    f.includes('total') ||
    f.includes('value') ||
    f.includes('deduction')
  )
}

function isNarrowField(field: string): boolean {
  const f = field.toLowerCase()
  return f === 'quantity' || f === 'scac' || f.includes('qty')
}

function isProField(field: string): boolean {
  const f = field.toLowerCase()
  return f.includes('pronumber') || f === 'pro'
}

function isWideField(field: string): boolean {
  const f = field.toLowerCase()
  return f.includes('description') || f.includes('itemdescription')
}

function isNegativeNumber(value: any): boolean {
  if (value === null || value === undefined || value === '') return false
  const n =
    typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''))
  return !Number.isNaN(n) && n < 0
}

function readByPath(row: any, path: string[]): any {
  let tmp = row
  for (let i = 0; i < path.length; i++) {
    if (tmp == null) return undefined
    tmp = tmp[path[i]]
  }
  return tmp
}

function toDeductionDisplay(value: any): string {
  if (value === null || value === undefined || value === '') return ''
  const withSpaces = String(value).replace(/[_-]/g, ' ')
  const lower = withSpaces.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

/** Format a single raw cell value using the pre-computed column plan. */
function formatCell(
  raw: any,
  plan: ColumnPlan,
): { text: string; negative: boolean } {
  if (raw === null || raw === undefined) return { text: '', negative: false }
  if (plan.isCurrency) {
    if (typeof raw === 'number') {
      return { text: formatCurrency(raw), negative: raw < 0 }
    }
    const n = Number(String(raw).replace(/,/g, ''))
    return { text: String(raw), negative: !Number.isNaN(n) && n < 0 }
  }
  const str = String(raw)
  return { text: plan.isEnum ? toDeductionDisplay(str) : str, negative: false }
}

// ─── Card data builders ────────────────────────────────────────────────────────

function buildDocumentInfoRows(
  headerData: any,
  documentId?: string,
): CardRow[] {
  return [
    {
      label: 'Document number',
      value: documentId ?? headerData?.documentNumber ?? '',
    },
    { label: 'Document date', value: headerData?.documentDate ?? '' },
    {
      label: 'Deduction type',
      value: toDeductionDisplay(headerData?.deductionType ?? ''),
    },
    {
      label: 'Removal type',
      value: toDeductionDisplay(headerData?.removalType ?? ''),
    },
  ]
}

function buildDeductionInfoRows(
  headerData: any,
  chargebackType: string,
  paymentId?: string,
): CardRow[] {
  const rows: CardRow[] = [
    { label: 'Payment #', value: paymentId ?? headerData?.paymentNumber ?? '' },
  ]
  if (
    chargebackType === CHARGEBACK_TYPE_RTVS ||
    chargebackType === CHARGEBACK_TYPE_CBDC
  ) {
    rows.push({ label: 'Location', value: headerData?.locationId ?? '' })
    rows.push({
      label: 'Physical disposition',
      value: toDeductionDisplay(headerData?.physicalDisposition ?? ''),
    })
  }
  if (chargebackType === CHARGEBACK_TYPE_CB) {
    rows.push({
      label: 'Reference #',
      value: headerData?.returnAuthorization ?? '',
    })
    rows.push({ label: 'Location', value: headerData?.locationId ?? '' })
    rows.push({
      label: 'Physical disposition',
      value: toDeductionDisplay(headerData?.physicalDisposition ?? ''),
    })
  }
  return rows
}

function buildShippingInfoRows(
  headerData: any,
  chargebackType: string,
): CardRow[] {
  const rows: CardRow[] = [
    { label: 'Ship date', value: headerData?.shipDate ?? '' },
  ]
  if (chargebackType === CHARGEBACK_TYPE_RTVS) {
    rows.push({
      label: 'Return authorization',
      value: headerData?.returnAuthorization ?? '',
    })
    rows.push({ label: 'SCAC', value: headerData?.scac ?? '' })
    rows.push({ label: 'PRO #', value: headerData?.proNumber ?? '' })
  } else if (chargebackType === CHARGEBACK_TYPE_CBDC) {
    rows.push({
      label: 'Return authorization',
      value: headerData?.returnAuthorization ?? '',
    })
    rows.push({ label: 'PRO#', value: headerData?.proNumber ?? '' })
  } else {
    rows.push({ label: 'SCAC', value: headerData?.scac ?? '' })
    rows.push({ label: 'PRO #', value: headerData?.proNumber ?? '' })
  }
  return rows
}

function buildAdjustmentsRows(
  headerData: any,
  chargebackType: string,
): CardRow[] {
  const deductionCosts = headerData?.deductionCosts ?? 0
  const handlingFee = headerData?.handlingFeeCosts ?? 0
  const total = headerData?.totalDeduction ?? 0
  return [
    {
      label: 'Deduction costs',
      value: formatCurrency(deductionCosts),
      negative: isNegativeNumber(deductionCosts),
    },
    {
      label:
        chargebackType === CHARGEBACK_TYPE_CB
          ? 'Handling/freight cost'
          : 'Handling fee costs',
      value: formatCurrency(handlingFee),
      negative: isNegativeNumber(handlingFee),
    },
    {
      label: 'Total deduction',
      value: formatCurrency(total),
      negative: isNegativeNumber(total),
      bold: true,
    },
  ]
}

function buildCards(
  headerData: any,
  chargebackType: string,
  documentId?: string,
  paymentId?: string,
): Card[] {
  return [
    {
      title: 'Document information',
      rows: buildDocumentInfoRows(headerData, documentId),
    },
    {
      title: 'Deduction information',
      rows: buildDeductionInfoRows(headerData, chargebackType, paymentId),
    },
    {
      title: 'Shipping information',
      rows: buildShippingInfoRows(headerData, chargebackType),
    },
    {
      title: 'Adjustments',
      rows: buildAdjustmentsRows(headerData, chargebackType),
    },
  ]
}

// ─── Table data ────────────────────────────────────────────────────────────────

/**
 * Proportional column widths (in pt) so narrow columns don't eat space.
 * Same weighting logic used by the previous @react-pdf implementation.
 */
function computeColWidthsPt(columns: ColumnDef[], contentW: number): number[] {
  const weights = columns.map((col) => {
    if (isWideField(col.field)) return 2.2
    if (isCurrencyField(col.field)) return 1.2
    if (col.field.toLowerCase() === 'style') return 1.2
    if (isProField(col.field)) return 2.0
    if (isNarrowField(col.field)) return 0.7
    return Math.max(0.8, Math.min(1.6, col.headerName.length / 12))
  })
  const total = weights.reduce((s, w) => s + w, 0) || 1
  return weights.map((w) => (w / total) * contentW)
}

function buildTableData(
  columns: ColumnDef[],
  rows: any[],
): { head: string[][]; body: string[][]; negatives: boolean[][] } {
  const head = [columns.map((c) => c.headerName)]
  const plan: ColumnPlan[] = columns.map((c) => ({
    path: c.field.split('.').filter(Boolean),
    isCurrency: isCurrencyField(c.field),
    isEnum:
      c.field === 'deductionType' ||
      c.field === 'removalType' ||
      c.field === 'physicalDisposition',
  }))

  const body: string[][] = []
  const negatives: boolean[][] = []
  for (let ri = 0; ri < rows.length; ri++) {
    const row = rows[ri]
    const cells: string[] = new Array(plan.length)
    const negs: boolean[] = new Array(plan.length)
    for (let ci = 0; ci < plan.length; ci++) {
      const { text, negative } = formatCell(
        readByPath(row, plan[ci].path),
        plan[ci],
      )
      cells[ci] = text
      negs[ci] = negative
    }
    body.push(cells)
    negatives.push(negs)
  }
  return { head, body, negatives }
}

// ─── Drawing helpers ───────────────────────────────────────────────────────────

const RED: [number, number, number] = [204, 0, 0]
const BLACK: [number, number, number] = [0, 0, 0]

// Target bullseye: outer red ring → white ring → red center dot.
function drawTargetLogo(doc: jsPDF, cx: number, cy: number): void {
  doc.setFillColor(204, 0, 0)
  doc.circle(cx, cy, 16, 'F')
  doc.setFillColor(255, 255, 255)
  doc.circle(cx, cy, 11, 'F')
  doc.setFillColor(204, 0, 0)
  doc.circle(cx, cy, 5.5, 'F')
}

function drawCard(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  card: Card,
  pad: number,
  titleH: number,
  rowH: number,
): void {
  // Card border
  doc.setDrawColor(204, 204, 204)
  doc.setLineWidth(0.5)
  doc.roundedRect(x, y, w, h, 3, 3, 'S')

  // Title
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(0, 0, 0)
  doc.text(card.title, x + pad, y + pad + 8)

  // Title underline
  doc.setDrawColor(238, 238, 238)
  doc.line(x + pad, y + pad + titleH, x + w - pad, y + pad + titleH)

  // Rows
  doc.setFontSize(8)
  let ry = y + pad + titleH + rowH
  for (const row of card.rows) {
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(68, 68, 68)
    doc.text(row.label, x + pad, ry)

    doc.setFont('helvetica', row.bold ? 'bold' : 'normal')
    const c = row.negative ? RED : BLACK
    doc.setTextColor(c[0], c[1], c[2])
    doc.text(String(row.value ?? ''), x + w - pad, ry, { align: 'right' })
    ry += rowH
  }
}

// ─── Main builder ──────────────────────────────────────────────────────────────

/**
 * Build the deduction PDF and return it as a Blob. Fully synchronous and fast.
 */
export function buildPdfBlob(params: GeneratePdfParams): Blob {
  const {
    treckHeader = [],
    rowData = [],
    headerData,
    chargebackType = CHARGEBACK_TYPE_CB,
    vendorId,
    documentId,
    paymentId,
    vendorName,
  } = params

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const M = 28
  const BOTTOM = 36
  const contentW = pageW - M * 2
  const cx = pageW / 2

  // ── Header: logo + titles ────────────────────────────────────────────────
  // Logo occupies y..y+32 (center at y+16, radius 16). Advance past the logo's
  // bottom edge plus a gap so the 14pt title's ascenders don't collide with it.
  let y = M + 4
  drawTargetLogo(doc, cx, y + 16)
  y += 32 + 22
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(0, 0, 0)
  doc.setFontSize(14)
  doc.text('Target Corporation', cx, y, { align: 'center' })
  y += 16
  doc.text('Inventory removal deduction detail', cx, y, { align: 'center' })
  y += 22

  // ── Vendor row ───────────────────────────────────────────────────────────
  // Prefer the header payload (matches the previous on-screen/PDF behaviour),
  // falling back to the top-level params when the header omits them.
  const vendorNumberText = String(headerData?.vendorNumber ?? vendorId ?? '')
  const vendorNameText = String(headerData?.vendorName ?? vendorName ?? '')
  doc.setFontSize(9)
  doc.setTextColor(0, 0, 0)
  doc.setFont('helvetica', 'bold')
  doc.text('Vendor number:', M, y)
  const vnLabelW = doc.getTextWidth('Vendor number: ')
  doc.setFont('helvetica', 'normal')
  doc.text(vendorNumberText, M + vnLabelW, y)

  const nameLabel = 'Vendor name:'
  doc.setFont('helvetica', 'bold')
  const nameLabelW = doc.getTextWidth(nameLabel + ' ')
  doc.setFont('helvetica', 'normal')
  const nameValueW = doc.getTextWidth(vendorNameText)
  const rightStart = pageW - M - (nameLabelW + nameValueW)
  doc.setFont('helvetica', 'bold')
  doc.text(nameLabel, rightStart, y)
  doc.setFont('helvetica', 'normal')
  doc.text(vendorNameText, rightStart + nameLabelW, y)
  y += 18

  // ── Info cards ───────────────────────────────────────────────────────────
  const cards = buildCards(headerData, chargebackType, documentId, paymentId)
  const gap = 8
  const cardW = (contentW - gap * 3) / 4
  const maxRows = cards.reduce((m, c) => Math.max(m, c.rows.length), 0)
  const pad = 8
  const titleH = 14
  const rowH = 12
  const cardH = pad + titleH + 6 + maxRows * rowH + pad
  const cardTop = y
  cards.forEach((card, i) => {
    drawCard(
      doc,
      M + i * (cardW + gap),
      cardTop,
      cardW,
      cardH,
      card,
      pad,
      titleH,
      rowH,
    )
  })
  y = cardTop + cardH + 18

  // ── Section label ────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(0, 0, 0)
  doc.text('Adjustments', M, y)
  y += 8

  // ── Table (fast path) ────────────────────────────────────────────────────
  const { head, body, negatives } = buildTableData(treckHeader, rowData)
  const colWidths = computeColWidthsPt(treckHeader, contentW)
  const columnStyles: Record<number, { cellWidth: number }> = {}
  colWidths.forEach((w, i) => {
    columnStyles[i] = { cellWidth: w }
  })

  autoTable(doc, {
    head,
    body,
    startY: y,
    margin: { top: M, left: M, right: M, bottom: BOTTOM },
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
      lineColor: [204, 204, 204],
      lineWidth: 0.5,
      textColor: [0, 0, 0],
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      lineColor: [68, 68, 68],
      lineWidth: 0.5,
    },
    bodyStyles: { fillColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [244, 244, 244] },
    columnStyles,
    showHead: 'everyPage',
    // Colour negative currency cells red (matches on-screen + previous PDF).
    didParseCell: (data: CellHookData) => {
      if (data.section !== 'body') return
      const rowNegs = negatives[data.row.index]
      if (rowNegs && rowNegs[data.column.index]) {
        data.cell.styles.textColor = RED
      }
    },
  })

  // ── Footer on every page (created date + page numbers) ────────────────────
  const createdDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  })
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(102, 102, 102)
    const fy = pageH - 18
    doc.setDrawColor(221, 221, 221)
    doc.setLineWidth(0.5)
    doc.line(M, fy - 7, pageW - M, fy - 7)
    doc.text(`Created: ${createdDate}`, M, fy)
    doc.text(`Page ${i} of ${pageCount}`, pageW - M, fy, { align: 'right' })
  }

  return doc.output('blob')
}
